import { useState } from "react";
import { API, getToken } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Download, X, Loader2 } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

// Preview an attached bill (image or PDF) with a Download action. Mobile-first.
// PDFs render every page stacked and scrollable via pdf.js (works on mobile browsers).
export default function BillViewer({ file, onClose }) {
  const [numPages, setNumPages] = useState(0);
  const [pdfError, setPdfError] = useState(false);
  if (!file) return null;
  const url = `${API}/files/${file.path}?auth=${getToken()}`;
  const isPdf = (file.content_type || "").includes("pdf") || (file.filename || "").toLowerCase().endsWith(".pdf");
  const name = file.filename || "Bill";
  return (
    <Dialog open={!!file} onOpenChange={(v) => { if (!v) { setNumPages(0); setPdfError(false); onClose(); } }}>
      <DialogContent className="max-w-[96vw] md:max-w-2xl p-0 overflow-hidden gap-0" data-testid="bill-viewer">
        <VisuallyHidden><DialogTitle>{name}</DialogTitle><DialogDescription>Bill preview</DialogDescription></VisuallyHidden>
        <div className="flex items-center justify-between px-3 h-14 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <span className="text-sm font-bold text-[#111827] truncate mr-2">{name}</span>
          <div className="flex items-center gap-1.5 shrink-0">
            <a href={url} download={name} target="_blank" rel="noreferrer" data-testid="bill-download"
              className="h-11 px-3 rounded-md bg-[#1E3A8A] text-white text-sm font-bold flex items-center gap-1.5">
              <Download className="w-4 h-4" /> Download
            </a>
            <button onClick={onClose} data-testid="bill-viewer-close" aria-label="Close"
              className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-[#E5E7EB] text-[#374151]">
              <X className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
        <div className="bg-[#111827] max-h-[80vh] overflow-auto flex flex-col items-center justify-start p-2 gap-2" data-testid="bill-body">
          {isPdf ? (
            pdfError ? (
              <div className="text-white text-sm text-center py-10 px-4">
                Preview unavailable. <a href={url} download={name} target="_blank" rel="noreferrer" className="underline font-bold">Download the bill</a> to view it.
              </div>
            ) : (
              <Document file={url} onLoadSuccess={({ numPages }) => setNumPages(numPages)} onLoadError={() => setPdfError(true)}
                loading={<div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-white" /></div>}
                className="w-full flex flex-col items-center gap-2">
                {Array.from({ length: numPages }, (_, i) => (
                  <div key={i} data-testid={`bill-pdf-page-${i + 1}`}>
                    <Page pageNumber={i + 1} width={Math.min(720, window.innerWidth - 24)}
                      renderTextLayer={false} renderAnnotationLayer={false}
                      className="shadow-lg" />
                  </div>
                ))}
              </Document>
            )
          ) : (
            <img src={url} alt="bill" className="max-w-full max-h-[78vh] object-contain" data-testid="bill-image" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
